import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { ConsultationServiceType } from '@tassomutuo-backoffice/types';
import { SERVICE_TYPE_LABEL } from './ConsultationServiceTypeChip';

interface IProps {
  q: string;
  serviceType: ConsultationServiceType | '';
  createdFrom: string;
  createdTo: string;
  onChangeQ: (value: string) => void;
  onChangeServiceType: (value: ConsultationServiceType | '') => void;
  onChangeCreatedFrom: (value: string) => void;
  onChangeCreatedTo: (value: string) => void;
  onReset: () => void;
}

const SERVICE_TYPE_OPTIONS: ConsultationServiceType[] = [
  'imprese',
  'agricoltura',
  'fondo-garanzia',
  'credito',
  'assicurazioni',
  'altro',
];

const ConsultationRequestsFiltersBar = ({
  q,
  serviceType,
  createdFrom,
  createdTo,
  onChangeQ,
  onChangeServiceType,
  onChangeCreatedFrom,
  onChangeCreatedTo,
  onReset,
}: IProps) => {
  const [localQ, setLocalQ] = useState(q);

  useEffect(() => {
    setLocalQ(q);
  }, [q]);

  const debouncedOnChangeQ = useMemo(
    () => _.debounce((value: string) => onChangeQ(value), 300),
    [onChangeQ]
  );

  useEffect(() => () => debouncedOnChangeQ.cancel(), [debouncedOnChangeQ]);

  const handleChangeQ = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setLocalQ(next);
      debouncedOnChangeQ(next);
    },
    [debouncedOnChangeQ]
  );

  const fromDate = createdFrom ? new Date(createdFrom) : null;
  const toDate = createdTo ? new Date(createdTo) : null;

  const handleChangeFrom = (value: Date | null) => {
    if (!value) {
      onChangeCreatedFrom('');
      return;
    }
    const start = new Date(value);
    start.setHours(0, 0, 0, 0);
    onChangeCreatedFrom(start.toISOString());
  };

  const handleChangeTo = (value: Date | null) => {
    if (!value) {
      onChangeCreatedTo('');
      return;
    }
    const end = new Date(value);
    end.setHours(23, 59, 59, 999);
    onChangeCreatedTo(end.toISOString());
  };

  const hasActiveFilters =
    !!q || !!serviceType || !!createdFrom || !!createdTo;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        px: 4,
        py: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <TextField
        size="small"
        placeholder="Cerca per nome, email o telefono"
        value={localQ}
        onChange={handleChangeQ}
        sx={{ minWidth: 280, flex: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="consultation-requests-service-type-label">
          Servizio
        </InputLabel>
        <Select
          labelId="consultation-requests-service-type-label"
          label="Servizio"
          value={serviceType}
          onChange={(e) =>
            onChangeServiceType(e.target.value as ConsultationServiceType | '')
          }
        >
          <MenuItem value="">
            <em>Tutti i servizi</em>
          </MenuItem>
          {SERVICE_TYPE_OPTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {SERVICE_TYPE_LABEL[value]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DatePicker
        label="Da"
        value={fromDate}
        onChange={handleChangeFrom}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true },
        }}
        maxDate={toDate ?? undefined}
      />

      <DatePicker
        label="A"
        value={toDate}
        onChange={handleChangeTo}
        slotProps={{
          textField: { size: 'small', sx: { minWidth: 160 } },
          field: { clearable: true },
        }}
        minDate={fromDate ?? undefined}
      />

      {hasActiveFilters && (
        <Button onClick={onReset} size="small" sx={{ textTransform: 'none' }}>
          Reset
        </Button>
      )}
    </Box>
  );
};

export default ConsultationRequestsFiltersBar;
